/**
 * Canonical tenant SiteAsset ingestion (VistoeraTASK-00408).
 *
 * Canvas OS-drop, Image Asset Picker, Website Builder, and Image Editor
 * save-as-new all POST the same `uploadSiteAsset` → `POST /workspaces/
 * {id}/assets` surface (multipart original + synchronous `create_asset`
 * on the origin — thumbnail inline, web variant lazy). There is NO
 * separate R2 direct-upload / finalize / rendition job queue on this
 * path; "92%" in the picker UI means "request bytes are on the wire /
 * awaiting the single POST response", not a second finalize hop.
 */

import { listSiteAssets, uploadSiteAsset, type SaasApiError } from './api'
import {
  SITE_ASSET_UPLOAD_PROGRESS_AWAITING_SERVER,
  SITE_ASSET_UPLOAD_PROGRESS_DONE,
  SITE_ASSET_UPLOAD_PROGRESS_INITIAL,
  siteAssetUploadProgressFromBytes,
} from './siteAssetUploadProgress'
import {
  attemptSiteAssetUploadReconciliation,
  DEFAULT_CANVAS_IMAGE_UPLOAD_CONCURRENCY,
  isAmbiguousUploadFailure,
} from './workspace/contentProduction/canvas/canvasMediaImportReconciliation'

/** Shared stability-first bound for every SiteAsset multipart ingestion. */
export const CANONICAL_SITE_ASSET_UPLOAD_CONCURRENCY = DEFAULT_CANVAS_IMAGE_UPLOAD_CONCURRENCY

export {
  SITE_ASSET_UPLOAD_PROGRESS_AWAITING_SERVER,
  SITE_ASSET_UPLOAD_PROGRESS_DONE,
  SITE_ASSET_UPLOAD_PROGRESS_INITIAL,
  siteAssetUploadProgressFromBytes,
} from './siteAssetUploadProgress'

export type SiteAssetUploadPhase = 'queued' | 'uploading' | 'processing' | 'done'

export type SiteAssetIngestHooks = {
  onProgress?: (percent: number, phase: SiteAssetUploadPhase) => void
  /** Fired once before ambiguous-failure list reconciliation (Canvas "确认中…"). */
  onReconciling?: () => void
}

/** One full ingestion attempt with ambiguous-failure reconciliation (no blind retry upload). */
export async function uploadSiteAssetIngest(
  workspaceId: string,
  siteId: string,
  file: File,
  hooks: SiteAssetIngestHooks = {},
  metadata?: Record<string, unknown>,
  placement?: { collection_id?: string | null },
): Promise<{ id: string }> {
  const { onProgress, onReconciling } = hooks
  onProgress?.(SITE_ASSET_UPLOAD_PROGRESS_INITIAL, 'queued')
  onProgress?.(SITE_ASSET_UPLOAD_PROGRESS_INITIAL, 'uploading')
  try {
    const { data } = await uploadSiteAsset(workspaceId, siteId, file, metadata, {
      collection_id: placement?.collection_id ?? null,
      onUploadProgress: (loaded, total) => {
        const percent = siteAssetUploadProgressFromBytes(loaded, total)
        const phase: SiteAssetUploadPhase =
          percent >= SITE_ASSET_UPLOAD_PROGRESS_AWAITING_SERVER ? 'processing' : 'uploading'
        onProgress?.(percent, phase)
      },
    })
    onProgress?.(SITE_ASSET_UPLOAD_PROGRESS_DONE, 'done')
    return { id: data.id }
  } catch (err) {
    if (!isAmbiguousUploadFailure(err)) throw err
    onProgress?.(SITE_ASSET_UPLOAD_PROGRESS_AWAITING_SERVER, 'processing')
    onReconciling?.()
    const match = await attemptSiteAssetUploadReconciliation(file, () =>
      listSiteAssets(workspaceId, siteId),
    )
    if (match) {
      onProgress?.(SITE_ASSET_UPLOAD_PROGRESS_DONE, 'done')
      return { id: match.id }
    }
    throw err
  }
}

export function siteAssetUploadErrorMessage(err: unknown, fallback: string): string {
  return (err as SaasApiError).message || fallback
}
